import Checkbox from '@src/components/common/Checkbox';
import DurationOverlayBadge from '@src/components/common/DurationOverlayBadge';
import EnlargedImageRenderProp from '@src/components/common/EnlargedImageRenderProp';
import {
  SPEAKER_FACE_PILE_TOOLTIP_ID,
  SpeakerFacePile,
} from '@src/components/common/SpeakerFacePile';
import { useBulkSelection } from '@src/hooks/useBulkSelection';
import {
  useDashboard,
  useDatoClient,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import DeleteIcon from '@src/svgs/DeleteIcon';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { ConfirmModal } from '../common/ConfirmModal';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../common/Table';
import { COMPACT_TOOLTIP_STYLE } from '../common/tooltipStyles';
import {
  buildContentItemsFromStories,
  contentTypeColors,
  contentTypeLabels,
  excludeInternalEditors,
  formatUpdatedDate,
  removeContentItemsFromStories,
} from './contentItems';
import { RecentContentFilters } from './RecentContentFilters';
import { SkeletonTableRows } from './SkeletonPrimitives';
import { useArborInternalEditorNames } from './useArborInternalEditorNames';
import { useBulkDeletion } from './useBulkDeletion';
import { useDropdown } from './useDropdown';
import { useRecentContentFilters } from './useRecentContentFilters';

import type { ContentItem, ContentType } from './contentItems';

type SortKey = 'title' | 'updated';

export const RecentContent = observer(() => {
  const dashboardStore = useDashboard();
  const datoClientStore = useDatoClient();
  const userIdentity = useUserIdentity();
  const { contentStudioEnableClipStatusAssignee } = useFlagsCombination(
    userIdentity.currentRole,
  );
  const videoCreator = useVideoCreatorStore();
  const internalEditorNames = useArborInternalEditorNames();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { homeStories, homeStoriesLoading } = dashboardStore;

  const allContentItems = useMemo(
    () =>
      excludeInternalEditors(
        buildContentItemsFromStories(homeStories),
        internalEditorNames,
      ),
    [homeStories, internalEditorNames],
  );

  const {
    searchQuery,
    setSearchQueryValue,
    activeTypeFilters,
    toggleTypeFilter,
    activeStoryFilter,
    setStoryFilter,
    activeUpdatedByFilter,
    setUpdatedByFilter,
    activeSpeakerFilter,
    setSpeakerFilter,
    activeStatusFilters,
    toggleStatusFilter,
    contentOwner,
    setContentOwner,
    myContentCount,
    allContentCount,
    filteredItems,
    displayedItems,
    hasMore,
    remainingCount,
    handleViewMore,
    clearAllFilters,
    activeFilterCount,
    uniqueStories,
    uniqueUpdatedBy,
    uniqueSpeakers,
  } = useRecentContentFilters(allContentItems, userIdentity.currentEditor);

  const typeDropdown = useDropdown();
  const statusDropdown = useDropdown();
  const storyDropdown = useDropdown({ resetQueryOnClose: true });
  const updatedByDropdown = useDropdown({ resetQueryOnClose: true });
  const speakerDropdown = useDropdown({ resetQueryOnClose: true });

  // Sorting
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const sortedItems = useMemo(() => {
    if (!sortKey) return displayedItems;
    const sorted = [...displayedItems];
    const dir = sortDirection === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      switch (sortKey) {
        case 'title':
          return dir * (a.title || '').localeCompare(b.title || '');
        case 'updated':
          return dir * (a.updatedAt.getTime() - b.updatedAt.getTime());
        default:
          return 0;
      }
    });
    return sorted;
  }, [displayedItems, sortKey, sortDirection]);

  // Bulk selection
  const {
    selectedIds,
    selectedCount,
    toggleItem,
    toggleAll,
    clearSelection,
    isSelected,
    isAllSelected,
  } = useBulkSelection();

  const {
    isDeleting,
    showDeleteConfirm,
    confirmDelete,
    cancelDelete,
    executeBulkDelete,
  } = useBulkDeletion({
    storyRepository: datoClientStore.storyRepository,
    getHomeStories: () => dashboardStore.homeStories,
    onDeleted: deleted => {
      const albumId = dashboardStore.homeStoriesAlbumId;
      if (!albumId) return;
      dashboardStore.setHomeStories(
        removeContentItemsFromStories(dashboardStore.homeStories, deleted),
        albumId,
      );
    },
  });

  const visibleIds = useMemo(
    () => sortedItems.map(item => item.id),
    [sortedItems],
  );

  const handleBulkDelete = useCallback(async () => {
    const itemsToDelete = allContentItems.filter(item =>
      selectedIds.has(item.id),
    );
    await executeBulkDelete(itemsToDelete, clearSelection);
  }, [allContentItems, selectedIds, executeBulkDelete, clearSelection]);

  const handleContentClick = useCallback(
    (item: ContentItem) => {
      // For clips, set the preview before navigating
      if (item.contentType === 'clip') {
        const story = homeStories.find(s => s.id === item.storyId);
        if (story) {
          const video = story.otherVideos?.find(v => v.id === item.id);
          if (video) {
            const activeVideo = video.videoFilePrimary
              ? video
              : video.associatedVideos?.find(v => v.videoFilePrimary);

            videoCreator.videoClipPreview = {
              ...video,
              selectedDimension: activeVideo?.aspectRatio,
            };
          }
        }
      }

      // Navigate to Content Studio
      const params = new URLSearchParams(searchParams);
      params.set('storyId', item.storyId);
      params.set('tab', item.tab);

      // For articles and social posts, pass the content timestamp key so BlogView
      // can deep-link to the exact saved item instead of falling back to the most recent.
      if (item.contentKey) {
        params.set('contentKey', item.contentKey);
      }

      // For quotes, pass the quote ID so PullQuotes opens the editor directly.
      if (item.contentType === 'quote') {
        params.set('quoteId', item.id);
      }

      navigate(`/content?${params.toString()}`);
    },
    [navigate, searchParams, homeStories, videoCreator],
  );

  if (homeStoriesLoading) {
    return (
      <Section data-testid="home-recent-content">
        <SectionHeaderRow>
          <SectionHeader>Recent Content</SectionHeader>
        </SectionHeaderRow>
        <SkeletonTableRows count={5} />
      </Section>
    );
  }

  if (allContentItems.length === 0) {
    return (
      <Section data-testid="home-recent-content">
        <SectionHeaderRow>
          <SectionHeader>Recent Content</SectionHeader>
        </SectionHeaderRow>
        <EmptyText>
          No content yet. Create clips, quotes, or articles to see them here!
        </EmptyText>
      </Section>
    );
  }

  return (
    <Section data-testid="home-recent-content">
      <SectionHeaderRow>
        <SectionHeader>Recent Content</SectionHeader>
        <RecentContentFilters
          activeTypeFilters={activeTypeFilters}
          toggleTypeFilter={toggleTypeFilter}
          activeStoryFilter={activeStoryFilter}
          setStoryFilter={setStoryFilter}
          activeUpdatedByFilter={activeUpdatedByFilter}
          setUpdatedByFilter={setUpdatedByFilter}
          activeSpeakerFilter={activeSpeakerFilter}
          setSpeakerFilter={setSpeakerFilter}
          activeStatusFilters={activeStatusFilters}
          toggleStatusFilter={toggleStatusFilter}
          showReviewWorkflow={contentStudioEnableClipStatusAssignee}
          contentOwner={contentOwner}
          setContentOwner={setContentOwner}
          myContentCount={myContentCount}
          allContentCount={allContentCount}
          uniqueStories={uniqueStories}
          uniqueUpdatedBy={uniqueUpdatedBy}
          uniqueSpeakers={uniqueSpeakers}
          currentEditorName={userIdentity.currentEditor}
          activeFilterCount={activeFilterCount}
          clearAllFilters={clearAllFilters}
          searchQuery={searchQuery}
          setSearchQueryValue={setSearchQueryValue}
          typeDropdown={typeDropdown}
          statusDropdown={statusDropdown}
          storyDropdown={storyDropdown}
          updatedByDropdown={updatedByDropdown}
          speakerDropdown={speakerDropdown}
        />
      </SectionHeaderRow>

      {selectedCount > 0 && (
        <BulkActionBar>
          <BulkActionText>
            {selectedCount} item{selectedCount !== 1 ? 's' : ''} selected
          </BulkActionText>
          <BulkActionButtons>
            <ClearSelectionButton
              onClick={clearSelection}
              disabled={isDeleting}
            >
              Clear selection
            </ClearSelectionButton>
            <DeleteSelectedButton onClick={confirmDelete} disabled={isDeleting}>
              <DeleteIcon width="16" height="16" strokeColor="currentColor" />
              {isDeleting ? 'Deleting...' : 'Delete'}
            </DeleteSelectedButton>
          </BulkActionButtons>
        </BulkActionBar>
      )}

      <Table data-testid="home-content-table">
        <TableHeader>
          <TableRow>
            <CheckboxHead>
              <Checkbox
                label=""
                color={
                  isAllSelected(visibleIds)
                    ? 'var(--primary)'
                    : 'var(--background)'
                }
                borderColor={
                  isAllSelected(visibleIds) ? 'var(--primary)' : 'var(--border)'
                }
                onClick={() => toggleAll(visibleIds)}
                checked={isAllSelected(visibleIds)}
                disabled={isDeleting}
              />
            </CheckboxHead>
            <TitleHead
              sortDirection={sortKey === 'title' ? sortDirection : null}
              onSort={() => handleSort('title')}
            >
              Content Title
            </TitleHead>
            <StoryHead>Story</StoryHead>
            <SpeakersHead>Speakers</SpeakersHead>
            <UpdatedByHead>Updated By</UpdatedByHead>
            <TypeHead>Type</TypeHead>
            <UpdatedHead
              sortDirection={sortKey === 'updated' ? sortDirection : null}
              onSort={() => handleSort('updated')}
            >
              Updated
            </UpdatedHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedItems.map((item, index) => (
            <TableRow
              key={item.id}
              data-testid={`home-content-row-${item.id}`}
              onClick={() => handleContentClick(item)}
              isLastRow={index === sortedItems.length - 1}
            >
              <CheckboxCol>
                <CheckboxClickArea
                  onClick={e => {
                    e.stopPropagation();
                    if (!isDeleting) toggleItem(item.id);
                  }}
                >
                  <Checkbox
                    label=""
                    color={
                      isSelected(item.id)
                        ? 'var(--primary)'
                        : 'var(--background)'
                    }
                    borderColor={
                      isSelected(item.id) ? 'var(--primary)' : 'var(--border)'
                    }
                    onClick={() => {}}
                    checked={isSelected(item.id)}
                    disabled={isDeleting}
                  />
                </CheckboxClickArea>
              </CheckboxCol>
              <TitleCol>
                <TitleCellContent>
                  {item.thumbnail ? (
                    <EnlargedImageRenderProp
                      src={item.thumbnail}
                      smallPhotoHeight={58}
                      enlargedPhotoHeight={180}
                      direction="right"
                      isVideo={item.contentType === 'clip'}
                    >
                      {({
                        setEnlargedState,
                        renderEnlargedImage,
                        enlargedState,
                      }) => (
                        <>
                          <ThumbnailWrapper
                            onMouseEnter={e =>
                              setEnlargedState(
                                e.currentTarget.getBoundingClientRect(),
                              )
                            }
                            onMouseLeave={() => setEnlargedState(null)}
                          >
                            <Thumbnail src={item.thumbnail} alt={item.title} />
                            {item.duration && (
                              <DurationOverlayBadge $size="small">
                                {item.duration}
                              </DurationOverlayBadge>
                            )}
                          </ThumbnailWrapper>
                          {enlargedState && renderEnlargedImage()}
                        </>
                      )}
                    </EnlargedImageRenderProp>
                  ) : (
                    <NoThumbnail>
                      {item.duration && (
                        <DurationOverlayBadge $size="small">
                          {item.duration}
                        </DurationOverlayBadge>
                      )}
                    </NoThumbnail>
                  )}
                  <TitleBlock>
                    <TitleText>{item.title}</TitleText>
                    {item.description && (
                      <DescriptionText>{item.description}</DescriptionText>
                    )}
                  </TitleBlock>
                </TitleCellContent>
              </TitleCol>
              <StoryCol>
                <StoryName>{item.storyTitle}</StoryName>
              </StoryCol>
              <SpeakersCol>
                <SpeakerFacePile speakers={item.speakers} />
              </SpeakersCol>
              <UpdatedByCol>
                <MutedText>{item.updatedBy}</MutedText>
              </UpdatedByCol>
              <TypeCol>
                <TypeBadge $type={item.contentType}>
                  {contentTypeLabels[item.contentType]}
                </TypeBadge>
              </TypeCol>
              <UpdatedCol>
                <MutedText>{formatUpdatedDate(item.updatedAt)}</MutedText>
              </UpdatedCol>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Tooltip
        id={SPEAKER_FACE_PILE_TOOLTIP_ID}
        style={COMPACT_TOOLTIP_STYLE}
        place="top"
        positionStrategy="fixed"
        noArrow
      />
      {filteredItems.length === 0 &&
        (searchQuery.trim() || activeFilterCount > 0) && (
          <EmptyText>
            {searchQuery.trim()
              ? `No results matching "${searchQuery}"`
              : 'No content matches the selected filters'}
          </EmptyText>
        )}
      {hasMore && (
        <ViewMoreButton onClick={handleViewMore}>
          View More ({remainingCount} remaining)
        </ViewMoreButton>
      )}

      {showDeleteConfirm && (
        <ConfirmModal
          closeModal={cancelDelete}
          onConfirm={handleBulkDelete}
          onCancel={cancelDelete}
          confirmText="Delete"
          cancelText="Cancel"
          title={`Delete ${selectedCount} content item${selectedCount !== 1 ? 's' : ''}?`}
          message="This cannot be undone."
        />
      )}
    </Section>
  );
});

/* ─── Layout ─── */

const Section = styled.div`
  margin-bottom: 48px;
`;

const SectionHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SectionHeader = styled.h2`
  font-size: 20px;
  font-weight: var(--font-weight-semibold);
  color: var(--foreground);
  margin: 0;
`;

/* ─── Bulk Action Bar ─── */

const BulkActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  margin-bottom: 12px;
  background-color: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  gap: 12px;
`;

const BulkActionText = styled.span`
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
`;

const BulkActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ClearSelectionButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: var(--text-sm);
  cursor: pointer;
  padding: 4px 8px;

  &:hover {
    color: var(--foreground);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DeleteSelectedButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background-color: var(--destructive);
  color: var(--background);
  border: none;
  border-radius: 6px;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: opacity var(--transition-fast);

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/* ─── Column widths (target: 1440px viewport) ─── */

const CheckboxHead = styled(TableHead)`
  width: 40px;
  min-width: 40px;
  max-width: 40px;
  vertical-align: middle;
`;
const TitleHead = styled(TableHead)`
  width: 32%;
  max-width: 480px;
`;
const StoryHead = styled(TableHead)`
  width: 16%;
  max-width: 260px;
`;
const SpeakersHead = styled(TableHead)`
  width: 10%;
  min-width: 90px;
`;
const UpdatedByHead = styled(TableHead)`
  width: 14%;
  min-width: 120px;
`;
const TypeHead = styled(TableHead)`
  width: 12%;
`;
const UpdatedHead = styled(TableHead)`
  width: 8%;
  min-width: 90px;
`;

const CheckboxCol = styled(TableCell)`
  width: 40px;
  min-width: 40px;
  max-width: 40px;
  vertical-align: middle;
`;
const CheckboxClickArea = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  height: 100%;
`;
const TitleCol = styled(TableCell)`
  width: 32%;
  max-width: 480px;
`;
const StoryCol = styled(TableCell)`
  width: 16%;
  max-width: 260px;
  overflow: hidden;
`;
const SpeakersCol = styled(TableCell)`
  width: 10%;
  min-width: 90px;
`;
const UpdatedByCol = styled(TableCell)`
  width: 14%;
  min-width: 120px;
  overflow: hidden;
`;
const TypeCol = styled(TableCell)`
  width: 12%;
`;
const UpdatedCol = styled(TableCell)`
  width: 8%;
  min-width: 90px;
`;

/* ─── Cell content ─── */

const TitleCellContent = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  min-width: 0;
`;

const ThumbnailWrapper = styled.div`
  position: relative;
  cursor: pointer;
  flex-shrink: 0;
`;

/* Thumbnail matches the Story Manager rows (102x58, rounded, cover). */
const Thumbnail = styled.img`
  width: 102px;
  height: 58px;
  object-fit: cover;
  border-radius: 8px;
  background-color: var(--background);
`;

const NoThumbnail = styled.div`
  position: relative;
  width: 102px;
  height: 58px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--border) 0%, var(--surface) 100%);
  flex-shrink: 0;
`;

/* Title + description stack vertically so the description reads as a sub-line
   of the title (mirrors the Story Manager TitleBlock). min-width: 0 lets both
   the title ellipsis and the description clamp work. */
const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const TitleText = styled.span`
  font-size: var(--text-sm);
  /* Bright foreground color carries the title hierarchy; normal weight keeps
     dense rows calm. See "Tables" in docs/design-system.md. */
  font-weight: var(--font-weight-normal);
  color: var(--foreground);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* Secondary, supporting context: smaller + dimmer than the title, clamped to
   two lines (mirrors the Story Manager Summary). */
const DescriptionText = styled.span`
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
  color: var(--text-secondary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StoryName = styled.span`
  /* display: block so the ellipsis engages within the fixed-width cell rather
     than the text overflowing into the next column. */
  display: block;
  font-size: var(--text-sm);
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TypeBadge = styled.span<{ $type: ContentType }>`
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  white-space: nowrap;
  color: ${props => contentTypeColors[props.$type].text};
`;

const MutedText = styled.span`
  /* display: block so the ellipsis engages within the fixed-width cell. */
  display: block;
  font-size: var(--text-xs);
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmptyText = styled.p`
  color: var(--text-muted);
  font-size: var(--text-sm);
`;

/* Secondary button, sm size — docs/design-system.md "Button Hierarchy". */
const ViewMoreButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: fit-content;
  margin: 16px auto 0;
  height: var(--btn-h-sm);
  padding: 0 var(--btn-px-sm);
  background-color: transparent;
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--foreground);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-primary);
  cursor: pointer;
  transition:
    background-color var(--transition-fast),
    border-color var(--transition-fast);

  &:hover {
    background-color: var(--surface-elevated);
    border-color: var(--text-muted);
  }
`;
